/**
 * Convenience Factory Methods for Universal Logger
 * 
 * This module provides convenient factory methods for common logger instantiation
 * patterns and simplifies the most frequent logger creation scenarios.
 */

import { LogNamespace } from './types';
import { getLoggerFactory } from './logger-factory';
import { INamespaceLogger, NamespaceContext } from './logger-factory';
import { getPreConfiguredFactory } from './factory-presets';

/**
 * Quick access to namespace-specific loggers with minimal configuration
 */
export class QuickLoggerFactory {
  /**
   * Get a logger for authentication operations
   */
  static auth(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.AUTH, context);
  }

  /**
   * Get a logger for API operations
   */
  static api(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.API, context);
  }

  /**
   * Get a logger for UI operations
   */
  static ui(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.UI, context);
  }

  /**
   * Get a logger for performance monitoring
   */
  static performance(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.PERFORMANCE, context);
  }

  /**
   * Get a logger for database operations
   */
  static database(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.DATABASE, context);
  }

  /**
   * Get a logger for classification operations
   */
  static classification(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.CLASSIFICATION, context);
  }

  /**
   * Get a logger for judge/evaluation operations
   */
  static judge(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.JUDGE, context);
  }

  /**
   * Get a logger for storage operations
   */
  static storage(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.STORAGE, context);
  }

  /**
   * Get a logger for worker operations
   */
  static worker(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.WORKER, context);
  }

  /**
   * Get a logger for metrics operations
   */
  static metrics(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.METRICS, context);
  }

  /**
   * Get a logger for security operations
   */
  static security(context?: Partial<NamespaceContext>): INamespaceLogger {
    return this.getOrCreateLogger(LogNamespace.SECURITY, context);
  }

  /**
   * Get or create a cached logger for a namespace
   */
  private static getOrCreateLogger(namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger {
    const factory = getPreConfiguredFactory();
    return factory.createLogger(namespace, context);
  }

  /**
   * Clear all cached loggers
   */
  static clearCache(): void {
    const factory = getLoggerFactory();
    if (factory.clearCache) {
      factory.clearCache();
    }
  }

  /**
   * Pre-warm all namespace loggers for better performance
   */
  static async preWarmAll(): Promise<void> {
    const factory = getLoggerFactory();
    const namespaces: LogNamespace[] = [
      LogNamespace.AUTH, LogNamespace.API, LogNamespace.UI, LogNamespace.PERFORMANCE, LogNamespace.DATABASE,
      LogNamespace.CLASSIFICATION, LogNamespace.JUDGE, LogNamespace.STORAGE, LogNamespace.WORKER, LogNamespace.METRICS, LogNamespace.SECURITY
    ];
    
    if (factory.preWarmLoggers) {
      await factory.preWarmLoggers(namespaces);
    }
  }
}

/**
 * Domain-specific logging patterns for comprehensive structured logging across all namespaces
 */
export class LoggerPatterns {
  
  // =============================================================================
  // API Namespace Patterns
  // =============================================================================
  
  /**
   * Log an API request with standard context
   */
  static logApiRequest(
    endpoint: string, 
    method: string, 
    statusCode?: number,
    duration?: number,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.api({
      customMetadata: { endpoint, method, statusCode, duration },
      ...context
    });

    const message = `${method} ${endpoint}`;
    const details = { endpoint, method, statusCode, duration };

    if (statusCode && statusCode >= 400) {
      logger.errorWithContext(message, details);
    } else if (statusCode && statusCode >= 300) {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  /**
   * Log LLM API calls with model and cost tracking
   */
  static logLlmApiCall(
    provider: string,
    model: string,
    operation: 'generation' | 'completion' | 'embedding' | 'classification',
    duration: number,
    cost?: number,
    tokens?: { input: number; output: number; total: number },
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.api({
      customMetadata: { provider, model, operation, cost, tokens },
      ...context
    });

    const message = `LLM API: ${provider}/${model} ${operation}`;
    const details = { provider, model, operation, duration, cost, tokens, error: error?.message };

    if (error) {
      logger.errorWithContext(message, details, error);
    } else if (duration > 10000) { // Slow response threshold
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  /**
   * Log API rate limiting events
   */
  static logRateLimit(
    endpoint: string,
    provider: string,
    limitType: 'requests' | 'tokens' | 'cost',
    current: number,
    limit: number,
    resetTime?: Date,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.api({
      customMetadata: { endpoint, provider, limitType, current, limit, resetTime },
      ...context
    });

    const message = `Rate limit ${limitType}: ${current}/${limit} for ${provider}`;
    const details = { endpoint, provider, limitType, current, limit, resetTime, usage: (current / limit) * 100 };

    if (current >= limit) {
      logger.errorWithContext(message, details);
    } else if (current / limit > 0.8) { // 80% threshold
      logger.warnWithContext(message, details);
    } else {
      logger.debugWithContext(message, details);
    }
  }

  // =============================================================================
  // Authentication Namespace Patterns
  // =============================================================================

  /**
   * Log authentication events with security context
   */
  static logAuthEvent(
    event: 'login' | 'logout' | 'failed_login' | 'password_reset' | 'token_refresh' | 'signup' | 'email_verification',
    userId?: string,
    details?: Record<string, any>,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.auth({
      userId,
      customMetadata: { event, ...details },
      ...context
    });

    const message = `Authentication event: ${event}`;
    const logDetails = { event, userId, ...details };

    if (event === 'failed_login') {
      logger.warnWithContext(message, logDetails);
    } else {
      logger.infoWithContext(message, logDetails);
    }
  }

  /**
   * Log session management activities
   */
  static logSessionActivity(
    activity: 'created' | 'refreshed' | 'expired' | 'invalidated' | 'hijack_detected',
    sessionId: string,
    userId?: string,
    duration?: number,
    reason?: string,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.auth({
      sessionId,
      userId,
      customMetadata: { activity, duration, reason },
      ...context
    });

    const message = `Session ${activity}: ${sessionId}`;
    const details = { activity, sessionId, userId, duration, reason };

    if (activity === 'hijack_detected' || activity === 'invalidated') {
      logger.errorWithContext(message, details);
    } else if (activity === 'expired') {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  /**
   * Log profile management operations
   */
  static logProfileOperation(
    operation: 'view' | 'update' | 'create' | 'delete' | 'api_key_save' | 'api_key_get',
    userId: string,
    fields?: string[],
    success: boolean = true,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.auth({
      userId,
      customMetadata: { operation, fields, success },
      ...context
    });

    const message = `Profile ${operation}: ${userId}`;
    const details = { operation, userId, fields, success, error: error?.message };

    if (!success || error) {
      logger.errorWithContext(message, details, error);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  // =============================================================================
  // UI Namespace Patterns
  // =============================================================================

  /**
   * Log user interactions with UI components
   */
  static logUserInteraction(
    action: 'click' | 'scroll' | 'focus' | 'input' | 'submit' | 'navigate' | 'error_boundary',
    component: string,
    page?: string,
    userId?: string,
    metadata?: Record<string, any>,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.ui({
      userId,
      customMetadata: { action, component, page, ...metadata },
      ...context
    });

    const message = `User ${action}: ${component}`;
    const details = { action, component, page, userId, ...metadata };

    if (action === 'error_boundary') {
      logger.errorWithContext(message, details);
    } else {
      logger.debugWithContext(message, details);
    }
  }

  /**
   * Log form submissions and validations
   */
  static logFormEvent(
    event: 'submit' | 'validation_error' | 'field_error' | 'success' | 'reset',
    formName: string,
    fields?: string[],
    errors?: Record<string, string>,
    userId?: string,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.ui({
      userId,
      customMetadata: { event, formName, fields, errors },
      ...context
    });

    const message = `Form ${event}: ${formName}`;
    const details = { event, formName, fields, errors, userId };

    if (event === 'validation_error' || event === 'field_error') {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  /**
   * Log page navigation and routing
   */
  static logNavigation(
    action: 'navigate' | 'redirect' | 'back' | 'forward' | 'refresh',
    from: string,
    to: string,
    userId?: string,
    loadTime?: number,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.ui({
      userId,
      customMetadata: { action, from, to, loadTime },
      ...context
    });

    const message = `Navigation ${action}: ${from} → ${to}`;
    const details = { action, from, to, userId, loadTime };

    logger.infoWithContext(message, details);
  }

  // =============================================================================
  // Database Namespace Patterns
  // =============================================================================

  /**
   * Log database operations with performance tracking
   */
  static logDatabaseOperation(
    operation: 'select' | 'insert' | 'update' | 'delete' | 'upsert',
    table: string,
    duration: number,
    recordCount?: number,
    filters?: Record<string, any>,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.database({
      customMetadata: { operation, table, duration, recordCount, filters },
      ...context
    });

    const message = `Database ${operation}: ${table}`;
    const details = { operation, table, duration, recordCount, filters, error: error?.message };

    if (error) {
      logger.errorWithContext(message, details, error);
    } else if (duration > 5000) { // Slow query threshold
      logger.warnWithContext(message, details);
    } else {
      logger.debugWithContext(message, details);
    }
  }

  /**
   * Log data migration and schema operations
   */
  static logMigrationEvent(
    event: 'start' | 'complete' | 'rollback' | 'error',
    migrationName: string,
    version?: string,
    duration?: number,
    recordsAffected?: number,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.database({
      customMetadata: { event, migrationName, version, duration, recordsAffected },
      ...context
    });

    const message = `Migration ${event}: ${migrationName}`;
    const details = { event, migrationName, version, duration, recordsAffected, error: error?.message };

    if (event === 'error' || error) {
      logger.errorWithContext(message, details, error);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  // =============================================================================
  // Performance Namespace Patterns
  // =============================================================================

  /**
   * Log performance metrics with thresholds
   */
  static logPerformanceMetric(
    operation: string,
    duration: number,
    threshold: number = 1000,
    metadata?: Record<string, any>,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.performance({
      customMetadata: { operation, duration, threshold, ...metadata },
      ...context
    });

    const message = `Performance: ${operation}`;
    const details = { operation, duration, threshold, exceeded: duration > threshold, ...metadata };

    if (duration > threshold * 2) {
      logger.errorWithContext(message, details);
    } else if (duration > threshold) {
      logger.warnWithContext(message, details);
    } else {
      logger.debugWithContext(message, details);
    }
  }

  /**
   * Log memory usage and resource monitoring
   */
  static logResourceUsage(
    resource: 'memory' | 'cpu' | 'network' | 'storage',
    current: number,
    limit: number,
    unit: string,
    operation?: string,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.performance({
      customMetadata: { resource, current, limit, unit, operation },
      ...context
    });

    const usage = (current / limit) * 100;
    const message = `Resource usage: ${resource} ${current}${unit}/${limit}${unit} (${usage.toFixed(1)}%)`;
    const details = { resource, current, limit, unit, usage, operation };

    if (usage > 90) {
      logger.errorWithContext(message, details);
    } else if (usage > 75) {
      logger.warnWithContext(message, details);
    } else {
      logger.debugWithContext(message, details);
    }
  }

  // =============================================================================
  // Judge Namespace Patterns
  // =============================================================================

  /**
   * Log evaluation workflow events
   */
  static logEvaluationEvent(
    event: 'start' | 'complete' | 'error' | 'metric_calculated' | 'comparison_complete',
    promptId: string,
    modelCount?: number,
    metrics?: string[],
    duration?: number,
    results?: Record<string, any>,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.judge({
      customMetadata: { event, promptId, modelCount, metrics, duration, results },
      ...context
    });

    const message = `Evaluation ${event}: ${promptId}`;
    const details = { event, promptId, modelCount, metrics, duration, results, error: error?.message };

    if (event === 'error' || error) {
      logger.errorWithContext(message, details, error);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  /**
   * Log model comparison and ranking results
   */
  static logModelComparison(
    promptId: string,
    models: string[],
    winner?: string,
    scores?: Record<string, number>,
    metrics: string[] = [],
    confidence?: number,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.judge({
      customMetadata: { promptId, models, winner, scores, metrics, confidence },
      ...context
    });

    const message = `Model comparison: ${promptId} (${models.length} models)`;
    const details = { promptId, models, winner, scores, metrics, confidence };

    logger.infoWithContext(message, details);
  }

  // =============================================================================
  // Classification Namespace Patterns
  // =============================================================================

  /**
   * Log AI model classification operations
   */
  static logClassificationOperation(
    operation: 'categorize' | 'validate' | 'retrain' | 'evaluate',
    inputType: 'prompt' | 'response' | 'batch',
    model: string,
    categories?: string[],
    confidence?: number,
    duration?: number,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.classification({
      customMetadata: { operation, inputType, model, categories, confidence, duration },
      ...context
    });

    const message = `Classification ${operation}: ${inputType} via ${model}`;
    const details = { operation, inputType, model, categories, confidence, duration, error: error?.message };

    if (error) {
      logger.errorWithContext(message, details, error);
    } else if (confidence !== undefined && confidence < 0.7) {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  // =============================================================================
  // Storage Namespace Patterns
  // =============================================================================

  /**
   * Log file operations and storage activities
   */
  static logStorageOperation(
    operation: 'upload' | 'download' | 'delete' | 'list' | 'copy' | 'move',
    path: string,
    size?: number,
    duration?: number,
    provider?: string,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.storage({
      customMetadata: { operation, path, size, duration, provider },
      ...context
    });

    const message = `Storage ${operation}: ${path}`;
    const details = { operation, path, size, duration, provider, error: error?.message };

    if (error) {
      logger.errorWithContext(message, details, error);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  // =============================================================================
  // Worker Namespace Patterns
  // =============================================================================

  /**
   * Log background job processing
   */
  static logWorkerJob(
    event: 'start' | 'complete' | 'failed' | 'retry' | 'timeout',
    jobType: string,
    jobId: string,
    duration?: number,
    attempt?: number,
    maxAttempts?: number,
    error?: Error,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.worker({
      customMetadata: { event, jobType, jobId, duration, attempt, maxAttempts },
      ...context
    });

    const message = `Worker job ${event}: ${jobType}/${jobId}`;
    const details = { event, jobType, jobId, duration, attempt, maxAttempts, error: error?.message };

    if (event === 'failed' || event === 'timeout' || error) {
      logger.errorWithContext(message, details, error);
    } else if (event === 'retry') {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }

  // =============================================================================
  // Metrics Namespace Patterns
  // =============================================================================

  /**
   * Log analytics and metrics collection
   */
  static logMetricsCollection(
    metric: string,
    value: number,
    unit: string,
    tags?: Record<string, string>,
    timestamp?: Date,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.metrics({
      customMetadata: { metric, value, unit, tags, timestamp },
      ...context
    });

    const message = `Metric: ${metric} = ${value}${unit}`;
    const details = { metric, value, unit, tags, timestamp };

    logger.debugWithContext(message, details);
  }

  // =============================================================================
  // Security Namespace Patterns
  // =============================================================================

  /**
   * Log security events with threat level
   */
  static logSecurityEvent(
    event: string,
    threatLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL',
    details?: Record<string, any>,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.security({
      customMetadata: { event, threatLevel, ...details },
      ...context
    });

    const message = `Security Event: ${event} [${threatLevel}]`;
    const logDetails = { event, threatLevel, ...details };

    switch (threatLevel) {
      case 'CRITICAL':
      case 'HIGH':
        logger.errorWithContext(message, logDetails);
        break;
      case 'MEDIUM':
        logger.warnWithContext(message, logDetails);
        break;
      case 'LOW':
      default:
        logger.infoWithContext(message, logDetails);
        break;
    }
  }

  /**
   * Log data access and privacy events
   */
  static logDataAccess(
    action: 'view' | 'export' | 'delete' | 'anonymize' | 'backup' | 'restore',
    dataType: 'user_profile' | 'api_keys' | 'evaluations' | 'prompts' | 'system_logs',
    userId?: string,
    adminId?: string,
    recordCount?: number,
    reason?: string,
    context?: Partial<NamespaceContext>
  ): void {
    const logger = QuickLoggerFactory.security({
      userId: userId || adminId,
      customMetadata: { action, dataType, adminId, recordCount, reason },
      ...context
    });

    const message = `Data access: ${action} ${dataType}`;
    const details = { action, dataType, userId, adminId, recordCount, reason };

    if (action === 'delete' || action === 'export') {
      logger.warnWithContext(message, details);
    } else {
      logger.infoWithContext(message, details);
    }
  }
}

/**
 * Operation-specific logger factories
 */
export class OperationLoggers {
  /**
   * Create a logger for HTTP operations
   */
  static createHttpLogger(baseUrl?: string): INamespaceLogger {
    return QuickLoggerFactory.api({
      customMetadata: { 
        type: 'http',
        baseUrl: baseUrl || 'unknown'
      }
    });
  }

  /**
   * Create a logger for async task operations
   */
  static createTaskLogger(taskName: string): INamespaceLogger {
    return QuickLoggerFactory.api({
      customMetadata: {
        type: 'task',
        taskName
      }
    });
  }

  /**
   * Create a logger for user session operations
   */
  static createSessionLogger(sessionId: string, userId?: string): INamespaceLogger {
    return QuickLoggerFactory.auth({
      sessionId,
      userId,
      customMetadata: {
        type: 'session'
      }
    });
  }

  /**
   * Create a logger for batch processing operations
   */
  static createBatchLogger(batchId: string, batchSize: number): INamespaceLogger {
    return QuickLoggerFactory.worker({
      customMetadata: {
        type: 'batch',
        batchId,
        batchSize
      }
    });
  }

  /**
   * Create a logger for database transaction operations
   */
  static createTransactionLogger(transactionId: string): INamespaceLogger {
    return QuickLoggerFactory.database({
      customMetadata: {
        type: 'transaction',
        transactionId
      }
    });
  }
}

/**
 * Environment-specific logger configurations
 */
export class EnvironmentLoggers {
  /**
   * Create a development environment logger with verbose logging
   */
  static createDevelopmentLogger(namespace: LogNamespace): INamespaceLogger {
    const factory = getLoggerFactory();
    return factory.createLogger(namespace, {
      customMetadata: {
        environment: 'development',
        verbose: true
      }
    });
  }

  /**
   * Create a production environment logger with optimized performance
   */
  static createProductionLogger(namespace: LogNamespace): INamespaceLogger {
    const factory = getLoggerFactory();
    return factory.createLogger(namespace, {
      customMetadata: {
        environment: 'production',
        optimized: true
      }
    });
  }

  /**
   * Create a testing environment logger with test context
   */
  static createTestLogger(namespace: LogNamespace, testSuite?: string): INamespaceLogger {
    const factory = getLoggerFactory();
    return factory.createLogger(namespace, {
      customMetadata: {
        environment: 'test',
        testSuite
      }
    });
  }
}

/**
 * Utilities for common logger operations
 */
export class LoggerUtils {
  /**
   * Create a logger with request context
   */
  static withRequestContext(
    namespace: LogNamespace,
    requestId: string,
    userId?: string,
    sessionId?: string
  ): INamespaceLogger {
    const factory = getLoggerFactory();
    return factory.createLogger(namespace, {
      requestId,
      userId,
      sessionId,
      customMetadata: {
        hasRequestContext: true
      }
    });
  }

  /**
   * Get factory statistics for all namespaces
   */
  static getFactoryStatistics(): any {
    const factory = getLoggerFactory();
    return {
      factoryStats: factory.getFactoryStats(),
      contextProviderStats: factory.getContextProviderStats?.(),
      cachedLoggers: Array.from(factory.getCachedLoggers().keys())
    };
  }

  /**
   * Dispose of all loggers and clear caches
   */
  static disposeAll(): void {
    const factory = getLoggerFactory();
    if (factory.disposeAll) {
      factory.disposeAll();
    }
    QuickLoggerFactory.clearCache();
  }

  /**
   * Create correlation IDs for request tracing
   */
  static generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Batch log multiple events efficiently
   */
  static batchLog(
    namespace: LogNamespace,
    events: Array<{
      level: 'debug' | 'info' | 'warn' | 'error';
      message: string;
      details?: Record<string, any>;
    }>,
    context?: Partial<NamespaceContext>
  ): void {
    const factory = getLoggerFactory();
    const logger = factory.createLogger(namespace, context);
    
    events.forEach(event => {
      switch (event.level) {
        case 'error':
          logger.errorWithContext(event.message, event.details);
          break;
        case 'warn':
          logger.warnWithContext(event.message, event.details);
          break;
        case 'info':
          logger.infoWithContext(event.message, event.details);
          break;
        case 'debug':
        default:
          logger.debugWithContext(event.message, event.details);
          break;
      }
    });
  }
} 